import PropTypes from "prop-types";
import { G } from "@mobily/ts-belt";
import * as React from "react";
import { actionCreators, useStateValue } from "../globalState";
import { getParameter, logger } from "../util";

const actionEndpoint = "/api/trackingPageAction";
const sessionEndpoint = "/api/trackingPageSession";

const service = {
  /*
   * sendTrackingAction sends a tracking action to the backend, for a given
   * trackingUUID, action and price.
   *
   * @param {object} initialization object { trackingUUID, action, price }
   */
  async sendTrackingAction({ trackingUUID, action, price, payload }) {
    try {
      const formData = new FormData();

      formData.set("uuid", trackingUUID);
      formData.set("action", action);

      if (price !== undefined) {
        formData.set("price", price);
      }

      if (G.isObject(payload)) {
        formData.set("payload", JSON.stringify(payload));
      }

      const response = await fetch(actionEndpoint, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to send tracking action: ${response.statusText}`,
        );
      }
    } catch (err) {
      logger.error(err);
    }
  },
  /*
   * getTrackingUUID returns a new trackingUUID for a session
   *
   * @returns {string | undefined} trackingUUID
   */
  async getTrackingUUID() {
    try {
      const formData = new FormData();
      const streamilyTrackingCode = getParameter("stc");

      formData.set("route", window.location.pathname);

      const getParameterByName = (name) => {
        const match = RegExp(`[?&]${name}=([^&]*)`).exec(
          window.location.search,
        );
        return match && decodeURIComponent(match[1].replace(/\+/g, " "));
      };
      formData.set("utm_campaign", getParameterByName("utm_campaign") || "");
      formData.set("utm_medium", getParameterByName("utm_medium") || "");
      formData.set("utm_source", getParameterByName("utm_source") || "");
      formData.set("rdt_cid", getParameterByName("rdt_cid") || "");
      formData.set("meta_ad_id", getParameterByName("stly_meta_ad_id") || "");
      formData.set(
        "meta_campaign_name",
        getParameterByName("stly_meta_campaign_name") || "",
      );
      formData.set(
        "meta_campaign_src",
        getParameterByName("stly_meta_campaign_src") || "",
      );

      if (streamilyTrackingCode && streamilyTrackingCode.length < 20) {
        formData.set("tracking_code", streamilyTrackingCode.trim());
      }

      const response = await fetch(sessionEndpoint, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to get tracking UUID");
      }

      const uuid = await response.text();
      return uuid;
    } catch (err) {
      logger.error(err);

      return null;
    }
  },
};

const trackingContext = React.createContext();

/*
 * useTracking allows using the trackingContext. Returns an object with the
 * shape: {
 *   track: function,
 *   refreshSession: function,
 *   setTrackingCode: function,
 * }
 */
export function useTracking() {
  return React.useContext(trackingContext);
}

/*
 * TrackingProvider maintains session information and provides functions to
 * track certain actions to children using the useTracking hook.
 */
const TrackingProvider = React.memo(function TrackingProvider({ children }) {
  const [state, dispatch] = useStateValue();

  /*
   * track allows tracking an action and a price
   *
   * @param {object} initialization object { action, price }
   */
  const track = React.useCallback(
    ({ action, price, payload }) => {
      if (!state.trackingUUID) {
        return;
      }

      service.sendTrackingAction({
        trackingUUID: state.trackingUUID,
        action,
        price,
        payload,
      });
    },
    [state.trackingUUID],
  );

  /*
   * refreshSession refreshes the trackingUUID in globalState. This is important
   * for distinguishing checkout vs regular pages.
   */
  const refreshSession = React.useCallback(async () => {
    try {
      const trackingUUID = await service.getTrackingUUID();
      dispatch(actionCreators.createSetTrackingUUID(trackingUUID));
    } catch {
      dispatch(actionCreators.createSetTrackingUUID(null));
    }
  }, [dispatch]);

  React.useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  /*
   * setTrackingCode retrives the trackingCode (if any) from query params and
   * sets it in globalState
   */
  const setTrackingCode = React.useCallback(() => {
    const streamilyTrackingCode = getParameter("stc");

    if (streamilyTrackingCode && streamilyTrackingCode.length < 20) {
      dispatch(actionCreators.createSetTrackingCode(streamilyTrackingCode));
    }
  }, [dispatch]);

  React.useEffect(() => {
    setTrackingCode();
  }, [setTrackingCode]);

  const value = React.useMemo(() => {
    return {
      track,
      refreshSession,
      setTrackingCode,
    };
  }, [track, refreshSession, setTrackingCode]);

  return (
    <trackingContext.Provider value={value}>
      {children}
    </trackingContext.Provider>
  );
});

TrackingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TrackingProvider;
